import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, XCircle, MinusCircle, Circle, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { assessmentApi, checklistApi, queryKeys } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  PASS:        { label: 'Đạt',           icon: <CheckCircle2 size={16} className="text-emerald-500"/>, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  PARTIAL:     { label: 'Đạt một phần',  icon: <MinusCircle  size={16} className="text-amber-500"/>, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  FAIL:        { label: 'Không đạt',     icon: <XCircle      size={16} className="text-rose-500"/>, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20' },
  NOT_CHECKED: { label: 'Chưa kiểm tra', icon: <Circle       size={16} className="text-slate-400"/>, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10' },
};

const PILLAR_LABELS: Record<string, string> = {
  I_LEGAL: 'Trụ cột I — Tuân thủ pháp lý',
  II_QUALITY: 'Trụ cột II — Chất lượng dữ liệu',
  III_SECURITY: 'Trụ cột III — An ninh, an toàn',
  IV_GOVERNANCE: 'Trụ cột IV — Tổ chức thực thi',
};

const PILLAR_COLORS: Record<string, string> = {
  I_LEGAL: 'border-indigo-500/50 bg-indigo-500/5',
  II_QUALITY: 'border-emerald-500/50 bg-emerald-500/5',
  III_SECURITY: 'border-amber-500/50 bg-amber-500/5',
  IV_GOVERNANCE: 'border-purple-500/50 bg-purple-500/5',
};

export default function ChecklistPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ itemId: string; score: string; notes: string } | null>(null);

  const { data: session } = useQuery({
    queryKey: queryKeys.sessions.detail(id!),
    queryFn:  () => assessmentApi.get(id!),
    enabled:  !!id,
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: queryKeys.checklist.bySession(id!),
    queryFn:  () => checklistApi.getBySession(id!),
    enabled:  !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (dto: { itemId: string; score: number; auditorNotes: string; status: string }) =>
      checklistApi.updateItem(id!, dto.itemId, { score: dto.score, auditorNotes: dto.auditorNotes, status: dto.status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.checklist.bySession(id!) });
      setEditing(null);
      toast.success('Đã cập nhật kết quả tiêu chí', {
        style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }
      });
    },
    onError: () => toast.error('Lỗi cập nhật', {
      style: { background: '#10101a', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }
    }),
  });

  const grouped = items.reduce<Record<string, typeof items>>((acc, item) => {
    const pillar = item.criterion?.pillar ?? 'UNKNOWN';
    if (!acc[pillar]) acc[pillar] = [];
    acc[pillar].push(item);
    return acc;
  }, {});

  const totalPassed  = items.filter(i => i.status === 'PASS').length;
  const totalFailed  = items.filter(i => i.status === 'FAIL').length;
  const totalPartial = items.filter(i => i.status === 'PARTIAL').length;
  const earned       = items.reduce((s, i) => s + Number(i.score), 0);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-slate-400">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm">Đang tải danh sách tiêu chí...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <Link 
          to={`/assessments/${id}`} 
          className="inline-flex items-center gap-1.5 text-xs font-black text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors mb-4 tracking-tight"
        >
          <ArrowLeft size={14} /> Quay lại hồ sơ {session?.sessionCode}
        </Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight font-heading">Danh sách tiêu chí</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1 font-bold">Đánh giá 22 tiêu chí tuân thủ dữ liệu quốc gia</p>
          </div>
          <div className="flex items-center gap-6 glass-panel px-6 py-3 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm">
            <span className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-black text-xs tracking-tight">
              <CheckCircle2 size={16} /> <span className="text-slate-900 dark:text-white">{totalPassed}</span> đạt
            </span>
            <span className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-black text-xs tracking-tight">
               <MinusCircle size={16} /> <span className="text-slate-900 dark:text-white">{totalPartial}</span> một phần
            </span>
            <span className="flex items-center gap-2 text-rose-600 dark:text-rose-400 font-black text-xs tracking-tight">
               <XCircle size={16} /> <span className="text-slate-900 dark:text-white">{totalFailed}</span> không đạt
            </span>
            <div className="w-[1px] h-6 bg-slate-200 dark:bg-white/10" />
            <span className="text-indigo-600 dark:text-indigo-400 font-black text-xl drop-shadow-[0_0_10px_rgba(99,102,241,0.3)]">
               {earned.toFixed(1)} <span className="text-xs text-slate-400 dark:text-slate-500 font-black">/100</span>
            </span>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="space-y-8">
        {Object.entries(PILLAR_LABELS).map(([pillar, pillarLabel]) => {
          const pillarItems = grouped[pillar] ?? [];
          const pillarScore = pillarItems.reduce((s, i) => s + Number(i.score), 0);
          const pillarMax   = pillarItems.reduce((s, i) => s + Number(i.maxScore), 0);
          
          if (pillarItems.length === 0) return null;

          return (
            <div key={pillar} className="glass-panel overflow-hidden border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm">
              {/* Pillar Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-l-4 ${PILLAR_COLORS[pillar]} border-b border-slate-100 dark:border-white/5`}>
                <h3 className="font-black text-slate-900 dark:text-white tracking-tight text-sm font-heading">{pillarLabel}</h3>
                <span className="text-xs font-black bg-slate-100 dark:bg-white/5 px-3 py-1 rounded-lg text-slate-900 dark:text-white shadow-inner tracking-tight">
                  {pillarScore.toFixed(1)} <span className="text-slate-400 dark:text-slate-500 font-bold">/ {pillarMax} điểm</span>
                </span>
              </div>

              {/* Items List */}
              <div className="divide-y divide-slate-100 dark:divide-white/5">
                {pillarItems.map((item) => {
                  const cfg = (STATUS_CONFIG as any)[item.status] || STATUS_CONFIG.NOT_CHECKED;
                  const isExpanded = expanded === item.id;
                  const isEditingThis = editing?.itemId === item.id;

                  return (
                    <div key={item.id} className="group transition-all bg-transparent">
                      {/* Row Handle */}
                      <div 
                        className="flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                        onClick={() => setExpanded(isExpanded ? null : item.id)}
                      >
                        <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 tracking-wider w-12 shrink-0">{item.criterion?.code}</span>
                        <div className="flex-1 text-[13px] font-black text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors tracking-tight leading-relaxed font-body">{item.criterion?.name}</div>
                        
                        <div className="flex items-center gap-4 shrink-0">
                          {item.criterion?.isAutomated && (
                            <span className="text-[10px] font-black bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded border border-indigo-500/20 tracking-tighter">
                              Auto
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-xl border tracking-tight ${cfg.bg} ${cfg.color} min-w-[120px] justify-center shadow-sm`}>
                            {cfg.icon} {cfg.label}
                          </span>
                          <span className="text-sm font-black text-slate-900 dark:text-white w-14 text-right">
                            {Number(item.score).toFixed(1)}<span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold ml-0.5">/{Number(item.maxScore)}</span>
                          </span>
                          <span className="w-5 flex justify-center text-slate-400 group-hover:text-indigo-500 transition-colors">
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </span>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden bg-slate-50/50 dark:bg-black/20 border-t border-slate-100 dark:border-white/5"
                          >
                            <div className="px-6 pb-6 pt-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 text-[13px]">
                                <div className="p-4 bg-white dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/5 shadow-inner">
                                  <p className="font-black text-indigo-600 dark:text-indigo-400 mb-3 flex items-center gap-2 tracking-tight text-xs">
                                    <FileText size={14} /> Yêu cầu tuân thủ
                                  </p>
                                  <p className="leading-relaxed text-slate-600 dark:text-slate-400 font-medium italic">{item.criterion?.complianceReq ?? '—'}</p>
                                </div>
                                <div className="p-4 bg-white dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/5 shadow-inner">
                                  <p className="font-black text-indigo-600 dark:text-indigo-400 mb-3 flex items-center gap-2 tracking-tight text-xs">
                                    <FileText size={14} /> Căn cứ pháp lý
                                  </p>
                                  <ul className="list-disc list-inside space-y-1.5 text-slate-500 dark:text-slate-400 marker:text-indigo-500 font-medium">
                                    {item.criterion?.legalReferences?.map((ref: string) => <li key={ref}>{ref}</li>)}
                                    {(!item.criterion?.legalReferences || item.criterion.legalReferences.length === 0) && (
                                      <li className="list-none text-slate-400 dark:text-slate-500 italic">—</li>
                                    )}
                                  </ul>
                                </div>
                              </div>

                              {/* Action Area */}
                              <div className="mt-8">
                                {isEditingThis ? (
                                  <div className="bg-white dark:bg-[#10101a] rounded-2xl border border-indigo-500/30 p-6 shadow-xl relative overflow-hidden">
                                    <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
                                    <h4 className="text-xs font-black text-slate-900 dark:text-white mb-6 tracking-tight font-heading">Cập nhật kết quả thẩm định</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
                                      <div className="md:col-span-4">
                                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-2">Trạng thái đánh giá</label>
                                        <select 
                                          className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-500/50 shadow-inner appearance-none"
                                          defaultValue={item.status}
                                          onChange={_e => setEditing(ed => ed ? { ...ed, score: ed.score } : null)}
                                        >
                                          {Object.keys(STATUS_CONFIG).map(s => (
                                            <option key={s} value={s} className="bg-white dark:bg-[#10101a] text-slate-900 dark:text-white font-medium">{(STATUS_CONFIG as any)[s].label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="md:col-span-2">
                                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-2">
                                          Điểm (Max: {item.maxScore})
                                        </label>
                                        <input 
                                          type="number" 
                                          className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-500/50 shadow-inner"
                                          min={0} max={Number(item.maxScore)} step={0.5}
                                          value={editing?.score}
                                          onChange={e => setEditing(ed => ed ? { ...ed, score: e.target.value } : null)}
                                        />
                                      </div>
                                      <div className="md:col-span-6">
                                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-tight mb-2">Ghi chú thẩm định viên</label>
                                        <textarea 
                                          rows={2} 
                                          className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-indigo-500/50 resize-none shadow-inner"
                                          value={editing?.notes}
                                          placeholder="Mô tả bằng chứng hoặc lý luận đánh giá..."
                                          onChange={e => setEditing(ed => ed ? { ...ed, notes: e.target.value } : null)}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-4">
                                      <button 
                                        className="px-8 py-3 rounded-xl bg-indigo-600 text-white font-black text-xs tracking-tight shadow-lg hover:shadow-indigo-500/30 transition-all active:scale-95"
                                        onClick={() => updateMutation.mutate({
                                          itemId: item.id,
                                          score: Number(editing!.score),
                                          auditorNotes: editing!.notes,
                                          status: Number(editing!.score) >= Number(item.maxScore) ? 'PASS'
                                                 : Number(editing!.score) > 0 ? 'PARTIAL' : 'FAIL',
                                        })}
                                        disabled={updateMutation.isPending}
                                      >
                                        {updateMutation.isPending ? 'Đang lưu...' : 'Lưu kết quả'}
                                      </button>
                                      <button 
                                        className="px-6 py-3 rounded-xl text-xs font-black text-slate-400 dark:text-slate-500 hover:text-rose-500 transition-all border border-transparent tracking-tight"
                                        onClick={() => setEditing(null)}
                                      >
                                        Huỷ bỏ
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                      {item.auditorNotes && (
                                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-4 shadow-inner">
                                          <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-tight mb-1.5">Ghi chú từ KTV</p>
                                          <p className="text-xs text-slate-600 dark:text-slate-300 italic font-medium">"{item.auditorNotes}"</p>
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      className="px-6 py-2.5 rounded-xl border border-indigo-500/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-black tracking-tight hover:bg-indigo-500/10 transition-all shadow-sm active:scale-95"
                                      onClick={() => setEditing({ itemId: item.id, score: String(item.score), notes: item.auditorNotes ?? '' })}
                                    >
                                      Cập nhật đánh giá
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
